import { useWatch } from 'react-hook-form';

import type { CoveragePricing } from '@/shared/types';
import type { Address } from '@/utils/types/renter.interface';

import type {
  OptionBoxOption,
  OptionBoxOptionsSource,
} from '@/modules/application-tracker/components/lease-form';
import { useDynamicCoverageRules } from '@/shared/hooks';

import { calculateLeaseDurationMonths } from '@/shared/utils/leaseDurationMonths.util';

import type { ScreeningResult } from './constants';
import {
  useSmbCoverageOptions,
  useTrackCoveragePricingResolved,
  useTrackScreeningLabelsLoaded,
} from './hooks';
import type { ScreeningResultOption } from './types';
import {
  buildEmptyOptions,
  hasCustomCoverageRuleLabels,
  resolveDynamicCoverageOptions,
} from './utils';
import {
  staticInstitutionalScreeningResultsOptions,
  staticSmbScreeningResultsOptions,
} from './screening-results.options';

// Builds the `option-box` options sources for the screening radios. Each is a
// `kind: 'hook'` source whose `useOptions` reads the relevant lease-info form
// fields and resolves the dynamic (DDS-priced / API-labelled) or static radio
// set — the same logic the legacy `InviteLeaseDetailsDrawerScreeningResults` ran
// inline, now expressed as engine option sources.

const toDate = (iso?: string | null): Date | undefined => (iso ? new Date(iso) : undefined);
const toNumber = (value?: string | null): number | undefined => {
  if (value == null || value === '') {
    return undefined;
  }
  const parsed = Number(String(value).replaceAll(',', ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const screeningOptionToOptionBox = (option: ScreeningResultOption): OptionBoxOption => ({
  id: option.id,
  value: option.value ?? '',
  label: option.text,
  subText: option.subText,
  tooltip: option.tooltip,
  disabled: option.disabled,
  disabledTooltip: option.disabledTooltip,
});

/** What each SMB tier option carries for the fields derived from it. */
export type SmbMonthsOptionData = {
  screeningResult: ScreeningResult;
  pricing?: CoveragePricing;
};

type PricingInputs = {
  address: Address | undefined;
  propertyId: string | undefined;
  addressSelected: boolean;
  leaseStart: Date | undefined;
  leaseEnd: Date | undefined;
  grossMonthlyRent: number | undefined;
  prepaidRent: number | undefined;
  freeRent: number | undefined;
};

// One scoped subscription to the lease inputs the coverage options are priced from.
const usePricingInputs = (): PricingInputs => {
  const [address, leaseStartDate, leaseEndDate, grossMonthlyRent, prepaidRent, freeRent] = useWatch(
    {
      name: [
        'address',
        'leaseStartDate',
        'leaseEndDate',
        'grossMonthlyRent',
        'prepaidRent',
        'freeRent',
      ],
    },
  ) as [Address | undefined, ...(string | null | undefined)[]];
  return {
    address,
    propertyId: address?.property_id,
    addressSelected: Boolean(address?.monolith_building_id),
    leaseStart: toDate(leaseStartDate),
    leaseEnd: toDate(leaseEndDate),
    grossMonthlyRent: toNumber(grossMonthlyRent),
    prepaidRent: toNumber(prepaidRent),
    freeRent: toNumber(freeRent),
  };
};

const useInstitutionalOptions = () => {
  const inputs = usePricingInputs();
  const { address, propertyId, addressSelected } = inputs;
  const { data, isLoading, isError, error } = useDynamicCoverageRules({
    propertyId,
    leaseStart: inputs.leaseStart,
    leaseEnd: inputs.leaseEnd,
    grossMonthlyRent: inputs.grossMonthlyRent,
    prepaidRent: inputs.prepaidRent,
    freeRent: inputs.freeRent,
    enabled: Boolean(propertyId),
  });

  const resolved = addressSelected
    ? resolveDynamicCoverageOptions(staticInstitutionalScreeningResultsOptions, data)
    : buildEmptyOptions(staticInstitutionalScreeningResultsOptions.length);

  const hasCustomLabels = hasCustomCoverageRuleLabels(data);
  const hasPricing = (data ?? []).some((rule) => Boolean(rule.pricing));
  useTrackScreeningLabelsLoaded({
    propertyId: propertyId ?? address?.monolith_building_id,
    isLoading,
    isError,
    error,
    hasCustomScreeningStatusLabels: hasCustomLabels,
    labelCount: addressSelected ? resolved.length : 0,
    enabled: !hasPricing,
  });
  useTrackCoveragePricingResolved({
    propertyId,
    enabled: hasPricing,
    isLoading,
    isError,
    error,
    hasCustomScreeningStatusLabels: hasCustomLabels,
    pricingOptionCount: data?.length ?? 0,
    pricingData: data,
  });

  return { resolved, isLoading: isLoading && addressSelected };
};

// Institutional: dynamic API labels/tooltips merged onto the static results,
// or locked placeholders until an address is chosen.
export const institutionalScreeningOptionsSource = (): OptionBoxOptionsSource => ({
  kind: 'hook',
  useOptions: () => {
    const { resolved, isLoading } = useInstitutionalOptions();
    return {
      options: resolved.map(screeningOptionToOptionBox),
      isLoading,
      // Match legacy invite behavior: when dynamic rules fail, keep rendering
      // fallback screening labels instead of surfacing the generic option-box
      // error state.
      isError: false,
    };
  },
});

// SMB legacy (no DDS pricing): the static Low/Medium/Max coverage radios.
export const smbStaticScreeningOptionsSource = (): OptionBoxOptionsSource => ({
  kind: 'static',
  options: staticSmbScreeningResultsOptions.map(screeningOptionToOptionBox),
});

const useSmbMonthsOptions = () => {
  const inputs = usePricingInputs();
  const [leaseGuaranteeMonthsOverride, selectedCoverageRuleId] = useWatch({
    name: ['leaseGuaranteeMonthsOverride', 'selectedCoverageRuleId'],
  }) as [string | null | undefined, string | null | undefined];

  const result = useSmbCoverageOptions({
    propertyId: inputs.propertyId,
    leaseStart: inputs.leaseStart,
    leaseEnd: inputs.leaseEnd,
    grossMonthlyRent: inputs.grossMonthlyRent,
    prepaidRent: inputs.prepaidRent,
    freeRent: inputs.freeRent,
    leaseDurationMonths: calculateLeaseDurationMonths(inputs.leaseStart, inputs.leaseEnd),
    selectedValue: toNumber(leaseGuaranteeMonthsOverride),
    selectedId: selectedCoverageRuleId ?? undefined,
    enabled: true,
  });

  useTrackCoveragePricingResolved({
    propertyId: inputs.propertyId,
    enabled: result.isUsingDynamicPricing,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    hasCustomScreeningStatusLabels: false,
    pricingOptionCount: result.coverageOptions.length,
    pricingData: result.coverageOptions,
  });

  return result;
};

// SMB new coverages: DDS-priced "N months / $X max coverage" radios (falls back
// to the static month radios when DDS omits pricing).
export const smbMonthsOptionsSource = (): OptionBoxOptionsSource => ({
  kind: 'hook',
  useOptions: () => {
    const { coverageOptions, isLoading } = useSmbMonthsOptions();
    return {
      options: coverageOptions.map((option) => ({
        id: option.id,
        value: option.value,
        label: option.text,
        subText: option.subText,
        disabled: option.disabled,
        data: {
          screeningResult: option.screeningResult,
          pricing: option.pricing,
        } satisfies SmbMonthsOptionData,
      })),
      isLoading,
      // `useSmbCoverageOptions` already falls back to static month options when
      // dynamic pricing isn't available (including API errors); do not replace
      // those fallback radios with a generic load-error message.
      isError: false,
    };
  },
});
